import {
  ICompanyFinancialsDataModel,
  KpiPeriod,
  KpiSection,
} from '../../../data-models/company-financials.data-model';
import { FormatterService } from '../../../util/formatter-service';
import { getRendererTypeFromFieldType, IField } from '../../../data-models/field2.data-model';
import { RendererType } from '../../../data-models/field-definition';
import { isDateString } from '../../../util/date-utilts';

export interface TypedFinancials extends Pick<
  ICompanyFinancialsDataModel,
  | 'id'
  | 'kpiId'
  | 'companyId'
  | 'period'
  | 'section'
  | 'date'
  | 'calculated'
  | 'fx_currency'
  | 'currencyUnresolved'
> {
  displayName: string;
  rendererType: RendererType | string;
  // all values come as strings, so parse if type is anything but string or date
  value: number | string | number[] | string[] | boolean | boolean[] | Date;
  valueFormatted: string;
}

export function getTypedFinancials(
  financials: ICompanyFinancialsDataModel[],
  kpiConfigMap: Map<number, IField<unknown>>
): TypedFinancials[] {
  // config.id corresponds to financials.kpiId
  return financials.reduce((acc, curr) => {
    const typedFinancial = financialToTypedFinancial(curr, kpiConfigMap);
    if (!typedFinancial) {
      return acc;
    }
    acc.push(typedFinancial);
    return acc;
  }, [] as TypedFinancials[]);
}

function kpiStringToTypedValue<T>(kpiField: IField<T>, value: string) {
  if (
    (kpiField.type === 'date' ||
      kpiField.formMeta?.renderer?.type === RendererType.date ||
      kpiField.formMeta?.formatter?.type === 'date' ||
      kpiField.formMeta?.formatter?.id === 'date') &&
    isDateString(value, RendererType.date)
  ) {
    return new Date(value);
  }
  if (kpiField.type === 'number') {
    return parseFloat(value);
  }
  return value;
}

export function createTypedFinancialsDataModel(overrides: Partial<TypedFinancials>): TypedFinancials {
  return {
    calculated: false,
    fx_currency: null,
    id: 0,
    kpiId: 0,
    companyId: 0,
    displayName: '',
    rendererType: RendererType.number,
    value: '',
    valueFormatted: '',
    date: '',
    period: KpiPeriod.month,
    section: KpiSection.actual,
    ...overrides,
  };
}

export function financialToTypedFinancial(
  financial: ICompanyFinancialsDataModel,
  kpiConfigMap: Map<number, IField<unknown>>
): TypedFinancials | null {
  const { kpiId, period, value, id, companyId, date, section } = financial;
  const configForParameter = kpiConfigMap.get(kpiId);

  if (!configForParameter) {
    return null;
  }

  try {
    const valueTyped = kpiStringToTypedValue(configForParameter, value);
    const formatter = FormatterService.get().getFormatterForField(configForParameter);
    const valueFormatted = formatter ? formatter(valueTyped) : value;

    const parameterDisplayName = configForParameter.displayName;

    return {
      calculated: financial.calculated,
      fx_currency: financial.fx_currency,
      currencyUnresolved: financial.currencyUnresolved,
      id,
      kpiId,
      companyId,
      displayName: parameterDisplayName,
      rendererType:
        configForParameter?.formMeta?.renderer?.type ?? getRendererTypeFromFieldType(configForParameter.type),
      value: valueTyped,
      valueFormatted,
      date: date.replace(/[T|\s].*/, ''),
      period,
      section,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
}
